import React from 'react';
import moment from 'moment';

import TagsInput from 'react-tagsinput';
import 'react-tagsinput/react-tagsinput.css';
import { CSVLink } from 'react-csv';
import {
  Badge,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';

import ComponentCard from '../../../components/ComponentCard';
import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';
import ActionButton from '../../../components/common/ActionButton';
import AppButton from '../../../components/common/AppButton';
import DataTable from '../../../components/common/DataTable';
import StatusBadge from '../../../components/common/StatusBadge';
import api from '../../../constants/api';
import { useLocaleConfig } from '../../../context/AppContext';
import { formatTableDate } from '../../../constants/time';
import { showSwal } from '../../../constants/swalConfig';

const list = (response) => response?.data?.data || response?.data || [];
const idOf = (item) => item?.id ?? item?.area_id ?? item?.city_id;
const cityIdOf = (item) => item?.city_id ?? item?.cityId;
const active = (value) =>
  Number(value) === 1 || value === true || value === '1';

const emptyCity = {
  city_name: '',
  city_is_active: 1,
};

const emptyArea = {
  city_id: '',
  area_name: '',
  area_is_active: 1,
};

export default function AreaManagement() {
  const { dateFormat } = useLocaleConfig();

  const [cities, setCities] = React.useState([]);
  const [areas, setAreas] = React.useState([]);
  const [selectedCityId, setSelectedCityId] = React.useState('');
  const [newAreas, setNewAreas] = React.useState([]);
  const [validation, setValidation] = React.useState('');
  const [cityValidation, setCityValidation] = React.useState('');
  const [cityModal, setCityModal] = React.useState(false);
  const [areaModal, setAreaModal] = React.useState(false);
  const [cityForm, setCityForm] = React.useState(emptyCity);
  const [areaForm, setAreaForm] = React.useState(emptyArea);
  const [citySearch, setCitySearch] = React.useState('');
  const [filters, setFilters] = React.useState({
    city: '',
    area: '',
    status: '',
  });

  const loadCities = () =>
    api
      .get('/servicearea/cities')
      .then((res) => setCities(list(res)))
      .catch(() => {});

  const loadAreas = () =>
    api
      .get('/servicearea/areas')
      .then((res) => setAreas(list(res)))
      .catch(() => {});

  const refreshData = () => Promise.all([loadCities(), loadAreas()]);

  React.useEffect(() => {
    refreshData();
  }, []);

  const cityAreaCount = (city) =>
    areas.filter(
      (area) =>
        String(cityIdOf(area)) === String(idOf(city)),
    ).length;

  const visibleCities = cities.filter((city) =>
    city.city_name
      ?.toLowerCase()
      .includes(citySearch.toLowerCase()),
  );

  const filteredAreas = areas.filter((area) => {
    const matchesCity =
      !filters.city ||
      String(cityIdOf(area)) === String(filters.city);

    const matchesArea =
      !filters.area ||
      area.area_name
        ?.toLowerCase()
        .includes(filters.area.toLowerCase());

    const matchesStatus =
      !filters.status ||
      String(active(area.area_is_active)) === filters.status;

    return matchesCity && matchesArea && matchesStatus;
  });

  const csvData = filteredAreas.map((area) => {
    const city = cities.find(
      (item) =>
        String(idOf(item)) === String(cityIdOf(area)),
    );

    return {
      City: area.city_name || city?.city_name || '',
      Area: area.area_name || '',
      Status: active(area.area_is_active)
        ? 'Active'
        : 'Inactive',
      'Added On': area.area_created_at
        ? moment(area.area_created_at).format(dateFormat)
        : '',
    };
  });

  const saveAreas = async () => {
    const cleanAreas = newAreas
      .map((name) => name.trim())
      .filter(Boolean);

    if (!selectedCityId || !cleanAreas.length) {
      setValidation('City and at least one Area are required.');
      return;
    }

    const normalizedNames = cleanAreas.map((name) =>
      name.toLocaleLowerCase(),
    );
    const duplicateNames = new Set(
      normalizedNames.filter(
        (name, index) =>
          normalizedNames.indexOf(name) !== index ||
          areas.some(
            (area) =>
              String(cityIdOf(area)) === String(selectedCityId) &&
              area.area_name?.trim().toLocaleLowerCase() === name,
          ),
      ),
    );

    if (duplicateNames.size) {
      const names = cleanAreas.filter((name) =>
        duplicateNames.has(name.toLocaleLowerCase()),
      );
      setValidation(
        `${names.join(', ')} ${names.length === 1 ? 'already exists' : 'already exist'} for this city.`,
      );
      return;
    }

    setValidation('');

    try {
      const results = await Promise.allSettled(
        cleanAreas.map((areaName) =>
          api.post('/servicearea/areas', {
            city_id: selectedCityId,
            area_name: areaName,
            area_is_active: 1,
          }),
        ),
      );

      const failed = results.reduce(
        (names, result, index) =>
          result.status === 'rejected'
            ? [...names, cleanAreas[index]]
            : names,
        [],
      );

      await refreshData();

      if (failed.length) {
        setNewAreas(failed);

        setValidation(
          `These areas failed: ${failed.join(
            ', ',
          )}. Saving again retries only these areas.`,
        );

        showSwal({
          title: 'Partially saved',
          text: `Failed areas: ${failed.join(', ')}`,
          icon: 'warning',
        });

        return;
      }

      setNewAreas([]);

      showSwal({
        title: 'Success',
        text: 'Areas saved successfully.',
        icon: 'success',
      });
    } catch (error) {
      setValidation(
        error?.response?.data?.message ||
          error.message ||
          'Unable to save areas.',
      );
    }
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;

    setSelectedCityId(cityId);

    if (
      cityId &&
      newAreas.some((name) => name.trim())
    ) {
      setValidation('');
    }
  };

  const handleAreasChange = (areaNames) => {
    setNewAreas(areaNames);

    if (
      selectedCityId &&
      areaNames.some((name) => name.trim())
    ) {
      setValidation('');
    }
  };

  const saveCity = () => {
    const cityName = cityForm.city_name.trim();

    if (!cityName) {
      return setCityValidation('City Name is required.');
    }

    const duplicateCity = cities.some(
      (city) =>
        String(idOf(city)) !== String(cityForm.id || '') &&
        city.city_name?.trim().toLocaleLowerCase() ===
          cityName.toLocaleLowerCase(),
    );

    if (duplicateCity) {
      return setCityValidation('This city already exists.');
    }

    const { id, ...payload } = cityForm;
    payload.city_name = cityName;

    const request = id
      ? api.put(`/servicearea/cities/${id}`, payload)
      : api.post('/servicearea/cities', payload);

    request
      .then(() => {
        setCityModal(false);
        setCityForm(emptyCity);
        setCityValidation('');
        refreshData();
      })
      .catch((error) =>
        setCityValidation(
          error?.response?.data?.message ||
            `Unable to ${id ? 'update' : 'save'} city.`,
        ),
      );

    return null;
  };

  const saveArea = () => {
    if (
      !areaForm.city_id ||
      !areaForm.area_name.trim()
    ) {
      return setValidation(
        'City and Area Name are required.',
      );
    }

    const { id, ...payload } = areaForm;

    api
      .put(`/servicearea/areas/${id}`, payload)
      .then(() => {
        setAreaModal(false);
        setAreaForm(emptyArea);
        refreshData();
      })
      .catch((error) =>
        setValidation(
          error?.response?.data?.message ||
            'Unable to update area.',
        ),
      );

    return null;
  };

  const deleteArea = (id) =>
    showSwal(
      {
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      },
      'delete',
    ).then((result) => {
      if (result.isConfirmed) {
        api
          .delete(`/servicearea/areas/${id}`)
          .then(refreshData)
          .catch((error) =>
            showSwal({
              title: 'Failed',
              text:
                error?.response?.data?.message ||
                'Unable to delete area.',
              icon: 'error',
            }),
          );
      }
    });

  const deleteCity = (id) => {
    const city = cities.find(
      (item) =>
        String(idOf(item)) === String(id),
    );

    const areaCount = cityAreaCount(city);

    const text = areaCount
      ? `Warning: Deleting ${city.city_name} will also delete all ${areaCount} associated areas. Are you sure you want to proceed?`
      : `Are you sure you want to delete ${
          city?.city_name || 'this city'
        }?`;

    showSwal(
      {
        title: 'Delete City?',
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      },
      'delete',
    ).then((result) => {
      if (result.isConfirmed) {
        api
          .delete(`/servicearea/cities/${id}`)
          .then(refreshData)
          .catch((error) =>
            showSwal({
              title: 'Cannot delete city',
              text:
                error?.response?.data?.message ||
                'This city has associated areas.',
              icon: 'error',
            }),
          );
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      area: '',
      status: '',
    });
  };

  const updateFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  /*
   * Only UI/table configuration.
   * No API/filter/edit/delete functionality is changed.
   */
  const areaColumns = React.useMemo(
    () => [
      {
        key: 'index',
        header: 'S.No',
        width: '8%',
        render: (_, index) => index + 1,
      },
      {
        key: 'city',
        header: 'City',
        width: '14%',
        render: (row) => {
          const city = cities.find(
            (item) =>
              String(idOf(item)) ===
              String(cityIdOf(row)),
          );

          return row.city_name || city?.city_name || '';
        },
      },
      {
        key: 'area_name',
        header: 'Area',
        width: '22%',
        render: (row) => row.area_name,
      },
      {
        key: 'status',
        header: 'Status',
        width: '14%',
        render: (row) => (
          <StatusBadge
            variant={active(row.area_is_active) ? 'success' : 'danger'}
            label={active(row.area_is_active) ? 'Active' : 'Inactive'}
          />
        ),
      },
      {
        key: 'area_created_at',
        header: 'Added On',
        width: '18%',
        render: (row) =>
          formatTableDate(row.area_created_at),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: '16%',
        render: (row) => (
          <div className="d-flex gap-2">
            <ActionButton
              action="edit"
              onClick={() => {
                setValidation('');

                setAreaForm({
                  id: idOf(row),
                  city_id: cityIdOf(row),
                  area_name: row.area_name || '',
                  area_is_active: active(
                    row.area_is_active,
                  )
                    ? 1
                    : 0,
                });

                setAreaModal(true);
              }}
            />

            <ActionButton
              action="delete"
              onClick={() =>
                deleteArea(idOf(row))
              }
            />
          </div>
        ),
      },
    ],
    [cities, dateFormat],
  );

  return (
    <>
      <BreadCrumbs />

      <div className="area-management-page">
      <Row>
        <Col sm="12">
          <ComponentCard>
            {/* ADD AREA + ADDED CITIES */}
            <Row>
              <Col md="6">
              <ComponentCard
  title={
    <div
      className="d-flex align-items-center"
      style={{ minHeight: '42px' }}
    >
      <h4 className="mb-0">Add Area</h4>
    </div>
  }
>
                  <FormGroup>
                    <Label className="app-text-label">
                      City{' '}
                      <small className="text-danger">
                        *
                      </small>
                    </Label>

                    <Input
                      type="select"
                      className="area-management-input"
                      value={selectedCityId}
                      onChange={handleCityChange}
                    >
                      <option value="">
                        Select City
                      </option>

                      {cities.map((city) => (
                        <option
                          key={idOf(city)}
                          value={idOf(city)}
                        >
                          {city.city_name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label className="app-text-label">
                      Areas{' '}
                      <small className="text-danger">
                        *
                      </small>
                    </Label>

                    <TagsInput
                      className="area-management-tags"
                      value={newAreas}
                      onChange={handleAreasChange}
                      addKeys={[
                        9,
                        13,
                        188,
                        ',',
                      ]}
                      addOnBlur
                      onlyUnique
                      tagProps={{
                        className:
                          'react-tagsinput-tag bg-info text-white rounded',
                      }}
                      inputProps={{
                        className:
                          'react-tagsinput-input area-management-input',
                        placeholder:
                          'Type an area name',
                      }}
                    />

                    <small className="text-muted">
                      Type and press enter to add
                      more areas.
                    </small>
                  </FormGroup>

                  {validation && (
                    <div className="text-danger mb-2">
                      {validation}
                    </div>
                  )}

                  <div className="area-filter-actions">
                    <AppButton
                      color="primary"
                      onClick={saveAreas}
                    >
                      Save Areas
                    </AppButton>

                    <AppButton
                      outline
                      onClick={() => {
                        setSelectedCityId('');
                        setNewAreas([]);
                        setValidation('');
                      }}
                    >
                      Reset
                    </AppButton>
                  </div>
                </ComponentCard>
              </Col>

              <Col md="6">
                <ComponentCard
                  title={
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span>
                        Added Cities{' '}
                        <Badge color="primary">
                          {cities.length}
                        </Badge>
                      </span>

                      <AppButton
                        label="+ Add City"
                        onClick={() => {
                          setCityValidation('');
                          setCityForm(emptyCity);
                          setCityModal(true);
                        }}
                      />
                    </div>
                  }
                >
                  <Input
                    className="mb-2"
                    placeholder="Search city..."
                    value={citySearch}
                    onChange={(event) =>
                      setCitySearch(
                        event.target.value,
                      )
                    }
                  />

                  {visibleCities.map((city) => (
                    <div
                      className="d-flex align-items-center justify-content-between border-bottom py-2"
                      key={idOf(city)}
                    >
                      <div>
                        <span className="fw-bold">
                          {city.city_name}
                        </span>

                        <small className="text-muted ms-2">
                          {cityAreaCount(city)} areas
                        </small>
                      </div>

                      <div className="d-flex gap-2">
                        <ActionButton
                          action="edit"
                          onClick={() => {
                            setCityValidation('');

                            setCityForm({
                              id: idOf(city),
                              city_name:
                                city.city_name || '',
                              city_is_active:
                                active(
                                  city.city_is_active,
                                )
                                  ? 1
                                  : 0,
                            });

                            setCityModal(true);
                          }}
                        />

                        <ActionButton
                          action="delete"
                          onClick={() =>
                            deleteCity(
                              idOf(city),
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </ComponentCard>
              </Col>
            </Row>

            {/* CITIES & AREAS LIST */}
            <div className="mt-4">
              <div className="mb-3">
                <h4 className="mb-0">
                  Cities & Areas List
                </h4>
              </div>

              {/* ALL FIVE CONTROLS IN ONE ROW */}
            
<div className="area-list-filters d-flex align-items-end flex-wrap flex-md-nowrap gap-3 mb-3">
  <div style={{ flex: '1 1 0px', minWidth: '160px' }}>
    <Label className="fw-semibold mb-1">Select City</Label>
    <Input
      type="select"
      value={filters.city}
      onChange={(event) => updateFilter('city', event.target.value)}
    >
      <option value="">All Cities</option>
      {cities.map((city) => (
        <option key={idOf(city)} value={idOf(city)}>
          {city.city_name}
        </option>
      ))}
    </Input>
  </div>

  <div style={{ flex: '1 1 0px', minWidth: '160px' }}>
    <Label className="fw-semibold mb-1">Search area</Label>
    <Input
      placeholder="Search area..."
      value={filters.area}
      onChange={(event) => updateFilter('area', event.target.value)}
    />
  </div>

  <div style={{ flex: '1 1 0px', minWidth: '160px' }}>
    <Label className="fw-semibold mb-1">Status</Label>
    <Input
      type="select"
      value={filters.status}
      onChange={(event) => updateFilter('status', event.target.value)}
    >
      <option value="">All Status</option>
      <option value="true">Active</option>
      <option value="false">Inactive</option>
    </Input>
  </div>

  <div className="area-filter-actions">
    <CSVLink filename="cities-and-areas.csv" data={csvData}>
      <AppButton color="primary">Export</AppButton>
    </CSVLink>

    <AppButton outline onClick={clearFilters}>Clear Filters</AppButton>
  </div>
</div>

              {/* TABLE WITHOUT ANOTHER COMPONENT CARD */}
              <DataTable
                columns={areaColumns}
                data={filteredAreas}
                mode="client"
                pageSize={10}
                searchable={false}
                emptyMessage="No cities / areas found."
                rowKey={(row) => idOf(row)}
              />
            </div>
          </ComponentCard>
        </Col>
      </Row>
      </div>

      {/* ADD CITY MODAL */}
      <Modal
        className="area-management-modal area-management-city-modal"
        isOpen={cityModal}
        toggle={() => setCityModal(false)}
      >
        <ModalHeader
          toggle={() => setCityModal(false)}
        >
          {cityForm.id
            ? 'Edit City'
            : 'Add City'}
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>
              City Name{' '}
              <small className="text-danger">
                *
              </small>
            </Label>

            <Input
              value={cityForm.city_name}
              onChange={(event) =>
                {
                  setCityForm({
                    ...cityForm,
                    city_name: event.target.value,
                  });
                  if (cityValidation) setCityValidation('');
                }
              }
            />
          </FormGroup>

          <FormGroup className="area-city-active d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              className="m-0"
              checked={active(
                cityForm.city_is_active,
              )}
              onChange={(event) =>
                setCityForm({
                  ...cityForm,
                  city_is_active:
                    event.target.checked
                      ? 1
                      : 0,
                })
              }
            />

            <Label className="mb-0">
              Active
            </Label>
          </FormGroup>

          {cityValidation && (
            <div className="text-danger mb-2">
              {cityValidation}
            </div>
          )}

          <div className="area-city-modal-actions d-flex justify-content-end gap-2">
            <AppButton
              color="primary"
              onClick={saveCity}
            >
              Save
            </AppButton>

            <AppButton
              outline
              onClick={() =>
                setCityModal(false)
              }
            >
              Cancel
            </AppButton>
          </div>
        </ModalBody>
      </Modal>

      {/* EDIT AREA MODAL */}
      <Modal
        className="area-management-modal"
        isOpen={areaModal}
        toggle={() => setAreaModal(false)}
      >
        <ModalHeader
          toggle={() => setAreaModal(false)}
        >
          Edit Area
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>City</Label>

            <Input
              type="select"
              value={areaForm.city_id}
              onChange={(event) =>
                setAreaForm({
                  ...areaForm,
                  city_id:
                    event.target.value,
                })
              }
            >
              {cities.map((city) => (
                <option
                  key={idOf(city)}
                  value={idOf(city)}
                >
                  {city.city_name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>
              Area Name{' '}
              <small className="text-danger">
                *
              </small>
            </Label>

            <Input
              value={areaForm.area_name}
              onChange={(event) =>
                setAreaForm({
                  ...areaForm,
                  area_name:
                    event.target.value,
                })
              }
            />
          </FormGroup>

          <FormGroup className="area-active-control d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              checked={active(
                areaForm.area_is_active,
              )}
              onChange={(event) =>
                setAreaForm({
                  ...areaForm,
                  area_is_active:
                    event.target.checked
                      ? 1
                      : 0,
                })
              }
            />

            <Label className="mb-0">
              Active
            </Label>
          </FormGroup>

          {validation && (
            <div className="text-danger mb-2">
              {validation}
            </div>
          )}

          <div className="area-filter-actions">
            <AppButton
              color="primary"
              onClick={saveArea}
            >
              Save
            </AppButton>

            <AppButton
              outline
              onClick={() =>
                setAreaModal(false)
              }
            >
              Cancel
            </AppButton>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
